import type {
  PartnerLocation as AddressLocation,
  PartnerAddressV2,
  PartnerRateCalendarEntry,
  ShiptimeDeliveryExperienceParameters,
  ShiptimeDesiredDeliveryDateOptions,
  SimulationServiceV1SimulationRawResultsListParams,
  SimulationServiceV1SimulationRawResultsListData,
  SimulationServiceV1SimulationRawResultsFilterOptionsListData,
} from '@shipium-corp/shipium-ts-types';
import type {
  HISTORICAL_DATASET_MODE,
  SHIPMENT_SELECTION_KEY,
  UPLOAD_DATASET_MODE,
} from '../../components/simulation/shared/SimulationShipmentSelectStep';
import type { TntJob, TntUsageType } from '../services/TimeInTransitService/types';

import type { ADD_MARGIN_SURCHARGE_MODIFIER_STEP_KEY } from '../../components/simulation/guidedSimulations/AddMarginSurchargeModifier';
import type { BASE_DETAILS_WORKFLOW_KEY } from '../../components/simulation/shared/SimulationBaseDetailsStep';
import type { CARRIER_METHOD_SELECT_STEP } from '../../components/simulation/guidedSimulations/CarrierMethodSelectStep';
import type { CARRIER_RATES_STEP_KEY } from '../../components/simulation/guidedSimulations/AddRatesStep';
import type { CARRIER_RATE_SELECT_STEP_KEY } from '../../components/simulation/guidedSimulations/CarrierRateSelectStep';
import type { CARRIER_SETTINGS_STEP_KEY } from '../../components/simulation/guidedSimulations/CarrierSettingsStep';
import type { CONTRACT_SELECT_STEP } from '../../components/simulation/shared/ContractSelectStep';
import type { CarrierSelectionSummary } from '../services/DatalakeService/types';
import type { FLAT_RATE_UPDATE_STEP_KEY } from '../../components/simulation/guidedSimulations/FlatRateUpdateStep';
import type { NEW_ORIGIN_MAPPING_STEP_KEY } from '../../components/simulation/guidedSimulations/OriginMappingStep';
import type { NEW_RATESHEET_STEP_KEY } from '../../components/simulation/guidedSimulations/NewRateSheetStep';
import type { ORIGIN_CREATE_STEP_KEY } from '../../components/simulation/guidedSimulations/OriginCreateStep';
import type { SIMULATION_CONTEXT_LINK_WORKFLOW_KEY } from '../../components/simulation/shared/SimulationContextLinkStep';
import type { SIMULATION_SUMMARY_WORKFLOW_KEY } from '../../components/simulation/shared/SimulationSummary';
import type { ScheduleEntry } from '../services/FP_FulfillmentContextService/types';
import type { TNT_OVERRIDE_STEP_KEY } from '../../components/simulation/shared/TntOverrideStep';

export type SimulationType =
  | 'advanced'
  | 'add_origin'
  | 'add_carrier_method'
  | 'flat_rate_update'
  | 'new_rate_sheet'
  | 'sell_rate_simulation';

export enum SimulationStatus {
  CONFIGURING = 'configuring',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  EXCEPTION = 'exception',
  CANCELLED = 'cancelled',
  ARCHIVED = 'archived',
  FINALIZING = 'finalizing',
}

export enum SimulationExecutionStatus {
  PENDING = 'pending',
  PAUSED = 'paused',
  ACTIVE = 'active',
}

export enum SimulationFulfillmentContextMappingTypes {
  CONTEXT_ID = 'context-id',
  HISTORICAL_CONTEXT = 'historical-context',
  ORIGIN_POSTAL_EXACT = 'origin-postal-exact',
  DESTINATION_POSTAL_PARTIAL = 'destination-postal-partial',
  ORIGIN_POSTAL_PARTIAL = 'origin-postal-partial',
  DESTINATION_POSTAL_EXACT = 'destination-postal-exact',
  CONTEXT_SEARCH = 'context-search',
}

export interface SimulationTntOverride {
  dataSetTypeId: string;
  name: string;
  description: string;
  createdBy: string;
  writeCompletedAt: string;
  usage: TntUsageType;
  jobId: string;
  jobStatus: TntJob['uploadJobStatus'];
  jobCreatedAt: string;
  jobUpdatedAt: string;
  jobErrors: string;
}
export interface SimulationTntOptions {
  timeInTransitSourcePreference?: string[];
  // adjustPastDesiredDeliveryDays is not in the Shiptime Simulation params
  // and does not appear to be used anywhere in simulation. Only used in Delivery Experience atm.
  // adjustPastDesiredDeliveryDays?:boolean;
  weekendConsideredTransitDay?: boolean;
  additionalDaysToProcess?: number;
  tntOverride?: string;
  timeInTransitSetting?: TimeInTransitSetting;
  timeInTransitModelSource?: TimeInTransitModelSource;
  // tntOverride?: SimulationTntOverride; // TODO add the entire TNT payload into the simulation. if we do this, it will need to be updated by a job to avoid stale info.
}

export type TimeInTransitSetting = NonNullable<ShiptimeDesiredDeliveryDateOptions['timeInTransitSetting']>;

export type TimeInTransitSourcePreference = NonNullable<
  ShiptimeDeliveryExperienceParameters['timeInTransitSourcePreference']
>[number];

export type TimeInTransitModelSource = 'shipment_delivery_prediction' | 'shipment_arrival_estimate';

export interface SimulationCostingOptions {
  totalUpgradeCostTolerance: number;
  upgradeOverCheapestCostTolerance: number;
}

export interface SimulationShipmentOptions {
  hazmat?: string;
  lithiumIon?: string;
  limitedQuantity?: string;
  ignoreRuleSets?: boolean;
  carrierServiceMethodAllowList?: string[];
  carrierServiceMethodExclusionList?: string[];
  ignoreCarrierServiceMethodAllowList?: boolean;
  shipOption?: string;
  destinationIsPOBox?: boolean;
  destinationAddressType?: string;
  desiredDeliveryDate?: string;
  timeInTransitDays?: number;
  businessDaysOfTransit?: number;
  shiptimeVersion?: string;
  costAdjustLimitEnabled?: boolean;
  costAdjustConfigId?: string;
  costAdjustLimitTargetCarrier?: string;
  costAdjustLimitExcludedCarriers?: string[];
  costAdjustLimitTargetPercent?: number;
  costAdjustLimitSeedAdjustment?: number;
  costAdjustLimitTargetBehavior?: 'above' | 'at' | 'below';
  originIds?: string[];
  tenantIds?: string[];
}

export enum CarrierListMode {
  ALLOW = 'allow',
  EXCLUDE = 'exclude',
}

export interface Simulation {
  simulationId: string;
  partnerId: string;
  name: string;
  description: string;
  simulationType: SimulationType;
  executionPriority: string;
  executionStatus: SimulationExecutionStatus;
  simulationStatus: SimulationStatus;
  includesHistoricalSelection: boolean;
  simulationStartDate: string;
  simulationEndDate: string;
  simulationSourceStartDate: string;
  simulationSourceEndDate: string;
  samplingPercentage?: number;
  historicalShipmentS3Locations?: string[] | null;
  createdBy: string;
  lastUpdatedBy: string;
  creationTimestamp: string;
  lastUpdatedTimestamp: string;
  simulationShipmentOptions?: SimulationShipmentOptions;
  simulationCostingOptions?: SimulationCostingOptions;
  simulationTntOptions?: SimulationTntOptions;
  simulationSetupData?: SimulationSetupData;
  visibleToPartner?: boolean;
  namespaceId?: string | null;
}

export type SimulationReportType =
  | 'carrier-selection-report'
  | 'simulation-summary'
  | 'carrier-selection-summary'
  | 'carrier-selection-detail'
  | 'reporting-filters'
  | 'time-in-transit-report'
  | 'simulation-choropleth-report'
  | 'sell-rate-margin-report'
  | 'otd-report'
  | 'cost-adjustment-report';

export interface FetchSimulationReportParams {
  reportType: SimulationReportType;
  simulationId: string;
  originSlug?: string;
  dayOfWeek?: string;
  carrier?: string;
  tntType?: string;
  tenantId?: string;
  sellCarrierRateId?: string;
  includeCarrierSurcharges?: boolean;
}

export type SimulationReportCollection = {
  [key in SimulationReportType]: SimulationReport;
};

export interface SimulationReport {
  simulationId: string;
  name: string;
  partnerId: string;
  reportType: SimulationReportType;
  reportOptions: {
    originSlug?: string;
    dayOfWeek?: string;
  };
  reportData: any;
}

export interface ChoroplethReportData {
  countyData: ChoroplethCountyRecord[];
  stateData: ChoroplethStateRecord[];
  originMarkerData: {
    originSlug: string;
    latitude: number;
    longitude: number;
    count: number;
  }[];
}

export interface ChoroplethCountyRecord extends ChoroplethStateRecord {
  countyFips: string;
  countyFipsAll: string[];
  countyName: string;
  population: number;
  density: number;
}

export interface ChoroplethStateRecord {
  count: number;
  stateId: string;
  stateName: string;
  totalLabelCost: number;
  averageLabelCost: number;
  primaryCarrierCount: number;
  primaryCarrier: string;
  primaryCarrierTotalLabelCost: number;
  primaryCarrierAverageLabelCost: number;
  primaryCarrierTimeInTransitDays?: number;
  carrierServiceMethods: {
    carrierServiceMethodId: string;
    count: number;
    totalLabelCost: number;
    averageLabelCost: number;
    p90TimeInTransitDays: number;
  }[];
  carriers: {
    [key: string]: {
      carrier: string;
      count: number;
      totalLabelCost: number;
      averageLabelCost: number;
      p90TimeInTransitDays: number;
    };
  };
}

// OTD report data. Types describe the camelCase shape the frontend consumes
// (server-side normalization converts from the snake-cased backend payload).
export type OtdReportDisplayMode = 'predicted_only' | 'full';

export type OtdReportCutKey =
  | 'serviceLevel'
  | 'carrier'
  | 'serviceMethod'
  | 'originState'
  | 'destinationState'
  | 'overTime'
  | 'packageWeight';

// One predicted-OTD risk bucket. `n` is the shipment count in the bucket.
// `historicalOtdPct` is the calibration cross-tab in full mode — the actual
// on-time rate for shipments in this predicted bucket. Absent when the bucket
// is empty (AVG over zero rows) or in predicted_only mode.
export interface OtdBucket {
  n: number;
  historicalOtdPct?: number;
}

// Predicted-OTD bucketing thresholds (matching the backend):
//   green:  p_new >= 0.80  — model is confident on-time
//   yellow: 0.50 <= p < 0.80 — model is uncertain
//   red:    p_new < 0.50  — model expects late
export interface OtdBuckets {
  green: OtdBucket;
  yellow: OtdBucket;
  red: OtdBucket;
}

export interface OtdReportCutRow {
  key: string;
  label: string;
  volume: number;
  predictedOtdPct: number;
  // Historical OTD % — real delivery outcome under the shipment's ORIGINAL
  // carrier method (not the sim's proposed choice). Only present in 'full'
  // display mode.
  historicalOtdPct?: number;
  // @deprecated Retained as a fallback while the backend rolls out the rename
  // (actual_otd_pct → historical_otd_pct). Callers should prefer
  // historicalOtdPct and fall back to actualOtdPct.
  actualOtdPct?: number;
  deltaPp?: number;
  // Per-shipment predicted OTD bucketed R/Y/G. Optional to accommodate the
  // rollout window before the buckets-shipping backend deploys.
  predictedBuckets?: OtdBuckets;
}

export interface OtdReportHeadline {
  displayMode: OtdReportDisplayMode;
  nShipmentsTotal: number;
  // Shipments in the report. Scoped to shipments the sim rerouted (sim's
  // carrier_service_method_id differs from historical). Unchanged shipments
  // are excluded because their predicted-vs-actual delta describes model
  // calibration rather than simulation impact.
  nShipmentsEvaluated: number;
  // Omitted by the backend when nShipmentsEvaluated is 0 — no shipments to
  // average over. Frontend keys off nShipmentsEvaluated to render the
  // empty-state message rather than the populated cards.
  predictedOtdPct?: number;
  historicalOtdPct?: number;
  // @deprecated — see OtdReportCutRow.actualOtdPct.
  actualOtdPct?: number;
  deltaPp?: number;
  predictedBuckets?: OtdBuckets;
}

export interface OtdReportData {
  headline: OtdReportHeadline;
  cuts: Record<OtdReportCutKey, OtdReportCutRow[]>;
}

export interface SimulationReportSummaryRecord {
  simulationAverageLabelCost: number;
  simulationTotalLabelCost: number;
  simulationP50LabelCost: number;
  simulationP75LabelCost: number;
  simulationP90LabelCost: number;
  simulationCount: number;
  historicalAverageLabelCost: number;
  historicalTotalLabelCost: number;
  historicalP50LabelCost: number;
  historicalP75LabelCost: number;
  historicalP90LabelCost: number;
  historicalCount: number;
  matchingHistoricalCount: number;
  averageLabelCostDifference: number;
  p50LabelCostDifference: number;
  p75LabelCostDifference: number;
  p90LabelCostDifference: number;
  totalLabelCostDifference: number;
  shipmentCountDifference: number;
  averageLabelCostDifferencePercentage: number;
  p50LabelCostDifferencePercentage: number;
  p75LabelCostDifferencePercentage: number;
  p90LabelCostDifferencePercentage: number;
  totalLabelCostDifferencePercentage: number;
  shipmentCountDifferencePercentage: number;
}

export interface SimulationReportCarrierSelectionSummaryRecord extends SimulationReportSummaryRecord {
  carrierServiceMethodId: string;
}

export interface SimulationReportingTableRecord {
  key: string;
  dimension: string;
  simulationValue: number;
  simulationPercentage?: number;
  historicalValue?: number;
  historicalPercentage?: number;
  differenceValue?: number;
  differencePercentage?: number;
}

export interface SimulationFilterCountRecord {
  tenantId: string;
  originSlug: string;
  carrier: string;
  dayOfWeek: string;
  tntType: string;
  shipmentCount: number;
}

export interface SimulationExportMetadata {
  bucket: string;
  key: string;
  // optional on v2 (S3 objects without a LastModified are passed through) — guard date renders
  lastModified?: string;
  sizeKb: number;
  name: string;
}

export interface SimulationShipmentBatch {
  simulationBatchId: string;
  simulationId: string;
  batchS3Location: string;
  batchStatus: string;
  responseS3Location: string;
  totalRows: number;
  successRows: number;
  errorRows: number;
  skippedRows: number;
  year: number;
  month: number;
  day: number;
  hour: number;
  createdBy: string;
  lastUpdatedBy: string;
  creationTimestamp: string;
  lastUpdatedTimestamp: string;
  performanceMetrics: {
    durationMs: number;
    p90DurationMs: number;
    activeJobCount: number;
    requestBatchSize: number;
    shipmentsPerSecond: number;
  };
}

export interface SimulationEvent {
  eventCreatedBy: string;
  eventTimestamp: string;
  simulationEventDetail: string;
  simulationEventId: string;
  simulationEventMetaData: any;
  simulationEventType: string;
  simulationId: string;
  simulationShipmentBatchId?: string;
}

export interface SimulationShipmentRawResult {
  key: string;
  simulationId: string;
  partnerId: string;
  shippedDateTime: string;
  orderedDateTime: string;
  referenceIdentifier: string;
  destinationState: string;
  desiredDeliveryDate: string;
  destinationPostalCode: string;
  destinationCountryCode: string;
  fromState: string;
  fromPostalCode: string;
  fromCountryCode: string;
  packagingMaterial: string;
  linearUnit: string;
  packageLength: number;
  packageWidth: number;
  packageHeight: number;
  totalWeight: number;
  totalWeightUnit: string;
  labelCost: number;
  historicalShipmentId: string;
  historicalCarrierServiceMethodId: string;
  historicalTimeInTransitDays: number;
  filename: string;
  request: string;
  response: string;
  error: string;
  status: string;
  fulfillmentContextId: string;
  simulationShipiumShipmentId: string;
  carrierDecisionCarrierServiceMethodId: string;
  carrierDecisionServiceMethodName: string;
  carrierDecisionCarrierServiceName: string;
  currencyCode: string;
  shippingCost: number;
  insuranceCost: number;
  accessorials: number;
  duty: number;
  taxes: number;
  totalCost: number;
  adjustedLabelCost: number;
  carrierCompareCost: number;
  carrierInvoiceCost: number;
  operationalCost: number;
  estimatedDeliveryDate: string;
  saturdayDeliveryRequested: boolean;
  timeInTransitDataType: string;
  rawTimeInTransit: number;
  timeInTransitModel: string;
  timeInTransitDays: number;
  duration: number;
}
export interface SimulationRawResultOptions {
  simulationId: string;
  date: string;
  hour: string;
  status: string;
  page: string;
}

export interface SimulationRawResult {
  options: {
    date: string;
    hour: string;
    status: string;
    limit: number;
    offset: number;
    count: number;
  };
  simulationShipments: SimulationShipmentRawResult[];
}

// v2 raw-results (simulation-service) read shapes, aliased from the generated ts-types.
export type SimulationRawResultsV2ListParams = SimulationServiceV1SimulationRawResultsListParams;
// The summary swapped carrierDecisionServiceMethodName (partner name) for
// carrierDecisionCarrierServiceMethodId (mapped to the real service-method name client-side).
// ts-types@2.0.85 still has the old shape, so widen locally — both the row and the list envelope —
// until a ts-types bump lands (bump currently blocked — see the ts-types/EULA note).
export type SimulationRawResultsV2Summary = Omit<
  SimulationServiceV1SimulationRawResultsListData['simulationShipments'][number],
  'carrierDecisionServiceMethodName'
> & { carrierDecisionCarrierServiceMethodId: string | null };
export type SimulationRawResultsV2ListData = Omit<
  SimulationServiceV1SimulationRawResultsListData,
  'simulationShipments'
> & { simulationShipments: SimulationRawResultsV2Summary[] };
export type SimulationRawResultsV2FilterOptions =
  SimulationServiceV1SimulationRawResultsFilterOptionsListData['filterOptions'];

export interface SimulationDetail extends Simulation {
  simulationShipmentBatches?: SimulationShipmentBatch[];
  simulationContextMappings?: SimulationContextMapping[];
  simulationBatchCreatorJobs?: any[];
  simulationEvents: SimulationEvent[];
  expectedBatches?: number;
  finalBatchStatusCount?: number;
  pendingBatchStatusCount?: number;
  totalShipmentCount?: number;
  cancelledShipmentCount?: number;
  failedShipmentCount?: number;
  completedShipmentCount?: number;
  errorShipmentCount?: number;
  skippedShipmentCount?: number;
  simulationShipmentOptions?: SimulationShipmentOptions;
  simulationTntOptions?: SimulationTntOptions;
  simulationSetupData: SimulationSetupData;
}

export interface SimulationSummary {
  fulfillmentProfiles: {
    fulfillmentProfileId: string;
    fulfillmentProfileCountry: string;
    fulfillmentProfilePostal: string;
  }[];
  simulationType: SimulationType;
  simulationStatus: SimulationStatus;
  executionStatus: SimulationExecutionStatus;
  shipmentCount: number;
  description: string;
  simulationId: string;
  name: string;
}

export interface CarrierSelectionTableSummary {
  shippedDate: string;
  carrierSelectionCount: number;
  contexts: Set<string>;
}

export interface SimulationUploadConfigData {
  selectedFiles: SimulationUpload[];
  end: string;
  start: string;
  mode: string;
  samplingPercentage: number;
}

export type SimulationUploadErrorType =
  | 'origin_postal_codes_limit_exceeded'
  | 'no_rows_found'
  | 'no_valid_rows_found'
  | 'invalid_file';

export interface SimulationUpload {
  s3Bucket: string;
  s3Key: string;
  originalFileName: string;
  uploadType: string;
  userId: string;
  partnerId: string;
  lastFileModificationTimestamp: string;
  details?: {
    processed: boolean;
    includesHistorical: boolean;
    maxBatchSize?: number;
    successCount?: number;
    failureCount?: number;
    shipmentCount?: number;
    minShipDate?: string;
    maxShipDate?: string;
    errorType?: SimulationUploadErrorType;
    origins?: {
      [postalCode: string]: {
        minShipDate: string;
        maxShipDate: string;
        shipmentCount: number;
      };
    };
    fulfillmentContexts?: {
      [fulfillmentContextId: string]: {
        minShipDate: string;
        maxShipDate: string;
        shipmentCount: number;
        fulfillmentContextName?: string;
      };
    };
  };
  mimeType: string;
  createdAt: string;
  updatedAt: string;
}

export interface SimulationContextMapping {
  mappingKey: string;
  simulationId: string;
  mappingValue: any;
  mappingType: SimulationFulfillmentContextMappingTypes;
  simulationFulfillmentContextId: string;
  createdBy: string;
  lastUpdatedBy: string;
  creationTimestamp: string;
  lastUpdatedTimestamp: string;
}

export interface SimulationContextMappingCreate {
  simulationId: string;
  mappingValue: any;
  mappingType: SimulationFulfillmentContextMappingTypes;
  simulationFulfillmentContextId: string;
}

export interface SimulationContext {
  simulationId: string;
  originalFulfillmentContextId: string;
  fulfillmentContextPostal: string;
  fulfillmentContextCountry: string;
  createdBy: string;
  lastUpdatedBy: string;
  creationTimestamp: string;
  lastUpdatedTimestamp: string;
  simulationFulfillmentContextId: string;
  isSimulationPassthrough: boolean;
}
export interface SimulationUpdate extends Partial<Simulation> {
  simulationId: string;
}

export interface SimulationBatchUpdate {
  simulationId: string;
  simulationBatchId: string;
  batchStatus: string;
}

export interface UploadedShipmentData {
  mode: typeof UPLOAD_DATASET_MODE;
  selectedFiles: SimulationUpload[];
  start: string;
  end: string;
  samplingPercentage: number;
}

export interface HistoricalShipmentSelectData {
  mode: typeof HISTORICAL_DATASET_MODE;
  carrierSelectionSummaries: CarrierSelectionSummary[];
  start: string;
  end: string;
  samplingPercentage: number;
  originIds?: string[];
  tenantIds?: string[];
}

export type NewOriginMappingType = 'postal_code_list' | 'distance';
export interface NewOriginMappingStepData {
  type: NewOriginMappingType;
  value: { postalCode: string; countryCode: string }[] | number;
}
export interface OriginCreateStepData {
  address: Partial<PartnerAddressV2 & AddressLocation>;
  schedule: Partial<ScheduleEntry>;
}
export interface FlatRateUpdateData {
  modifierType: 'discount' | 'increase';
  modifierValue: number;
  carrier: string;
  carrierServiceMethodId: string;
}
export interface NewRatesheetData {
  carrier: string;
  carrierServiceMethodId: string;
}
export interface CarrierMethodSelectStepData {
  carrier: string;
  serviceMethods: string[];
}

export interface CarrierRateSummary {
  name: string;
  carrierRateId: string;
  carrierId: string;
  carrierServiceMethodId: string;
}

export interface CarrierRateSelectData {
  carrierRates: CarrierRateSummary[];
}

export type CarrierSurchargeSellRateModiferType =
  | 'percentageDiscount'
  | 'percentageIncrease'
  | 'flatIncrease'
  | 'flatDiscount';

export interface CarrierSurchargeModiferStep {
  modifierType: CarrierSurchargeSellRateModiferType;
  amount: number;
  applyCarrierSurcharges: boolean;
}

export type SimulationServiceVersion = 'v1' | 'v2';
export const DEFAULT_SIMULATION_SERVICE_VERSION: SimulationServiceVersion = 'v2';

export interface SimulationSetupData {
  /**
   * Per-simulation service version, chosen once at create-time (default 'v2')
   * and locked thereafter. Read via useSimulationVersion(simulationId).
   */
  serviceVersion?: SimulationServiceVersion;
  [SHIPMENT_SELECTION_KEY]?: UploadedShipmentData | HistoricalShipmentSelectData;
  [TNT_OVERRIDE_STEP_KEY]?: SimulationTntOptions;
  [BASE_DETAILS_WORKFLOW_KEY]?: Simulation;
  [NEW_ORIGIN_MAPPING_STEP_KEY]?: NewOriginMappingStepData;
  [ORIGIN_CREATE_STEP_KEY]?: OriginCreateStepData;
  [CONTRACT_SELECT_STEP]?: string[];
  [FLAT_RATE_UPDATE_STEP_KEY]?: RateUpdate;
  [NEW_RATESHEET_STEP_KEY]?: RateUpdate;
  [CARRIER_METHOD_SELECT_STEP]?: CarrierMethodSelectStepData;
  // v2 persists an empty {} marker (type recovered from the mappings); v1 stores { mappingType }.
  // The array form is a legacy shape still read on resume.
  [SIMULATION_CONTEXT_LINK_WORKFLOW_KEY]?:
    | SimulationContextMappingCreate[]
    | { mappingType?: SimulationFulfillmentContextMappingTypes };
  // will type this when I handle
  // form repopulation
  [CARRIER_SETTINGS_STEP_KEY]?: any;
  [CARRIER_RATES_STEP_KEY]?: any;
  [SIMULATION_SUMMARY_WORKFLOW_KEY]?: void;
  [CARRIER_RATE_SELECT_STEP_KEY]?: CarrierRateSelectData;
  [ADD_MARGIN_SURCHARGE_MODIFIER_STEP_KEY]?: CarrierSurchargeModiferStep;
}

export interface RateUpdate {
  modifierValue?: number;
  modifierType?: string;
  carrier: string;
  carrierServiceMethodId: string;
  simulationId: string;
  rateCalendarEntry: Partial<PartnerRateCalendarEntry>;
  usingCarrierSelectionData: boolean;
}
export type SimulationShipmentBatchSummaryRecord = {
  date: string;
  shipmentCount: number;
  errorCount: number;
  hoursProcessed: number;
  hoursProcessedPercentage: number;
};
export interface SimulationShipmentBatchSummary {
  name: string;
  simulationId: string;
  partnerId: string;
  simulationType: string;
  samplingPercentage: number;
  lastUpdatedTimestamp: string;
  creationTimestamp: string;
  lastUpdated_by: string;
  createdBy: string;
  simulationSourceEndDate: string;
  simulationSourceStartDate: string;
  includesHistoricalSelection: boolean;
  simulationStatus: string;
  executionStatus: string;
  executionPriority: string;
  description: string;
  simulationShipmentBatchSummary: Record<string, SimulationShipmentBatchSummaryRecord>;
}
export interface HealthCheckResult {
  waiting: number;
  active: number;
  succeeded: number;
  failed: number;
  delayed: number;
  newestJob?: string;
}

export interface CreateSimulationBatchesReponse {
  simulationShipmentBatchCreatorQueueJobIds: string[];
  simulationShipmentBatchCreatorQueueStatus: HealthCheckResult;
  simulationSummary: SimulationShipmentBatchSummary;
  startDate: string;
  endDate: string;
}

export interface CreateOriginSimulationPayload {
  schedule: Partial<ScheduleEntry>;
  address: Partial<PartnerAddressV2>;
  contractIds: string[];
  simulationId: string;
  destinationPostalCodes: { postalCode: string; countryCode: string }[];
}

export interface CarrierAndMethodsSimulationPayload {
  carrierId: string;
  origins?: string[];
  tenants?: string[];
  postalCodes?: string[];
  usingCarrierSelectionData: boolean;
  simulationId: string;
}

export interface RateUpdateStartPayload extends RateUpdate {
  postalCodes: string[];
}

export interface SellRateSimulationPayload {
  simulationId: string;
  carrierRates: CarrierRateSummary[];
}

export interface SimulationSellRateMapping {
  simulationId: string;
  carrierRateId: string;
  carrierSettingsId: string;
  carrierId: string;
  carrierServiceMethodId: string;
  partnerRateName: string;
  includeCarrierSurcharges?: boolean;
  carrierSurchargeModifierValue?: number;
  carrierSurchargeModifierType?: CarrierSurchargeSellRateModiferType;
  creationTimestamp: string;
  lastUpdatedTimestamp: string;
}

export interface SellRateCarrierSelection {
  carrierServiceMethodId: string;
  sellRateZoneId: string;
  simulationAverageLabelCost: number;
  simulationTotalLabelCost: number;
  simulationAverageSellRateCost: number;
  simulationTotalSellRateCost: number;
  simulationTotalBaseSellRateCost: number;
  simulationCount: number;
  negativeMarginCount: number;
  averageMargin: number;
  totalMargin: number;
  averageSellRateCostDifferencePercentage: number;
  totalSellRateCostDifferencePercentage: number;
}

export interface MarginSimulationReportData {
  averageSellRateMargin: number;
  totalSellRateMargin: number;
  totalSimulationLabelCount: number;
  totalSimulationLabelCost: number;
  averageSimulationLabelCost: number;
  totalSimulationSellRateCost: number;
  totalSellRateMarginPercent: number;
  averageSellRateCost: number;
  sellRateCarrierSelections: SellRateCarrierSelection[];
  totalHistoricalCost: number;
  totalHistoricalSavings: number;
  averageHistoricalCost: number;
  averageHistoricalSavings: number;
  historicalSavingsPercent: number;
  totalSimulationSellRateSurchargeCost: number;
  totalSimulationSellRateBaseCost: number;
}

export interface SimulationSellRateZoneMapping {
  zoneId: string;
  count: number;
  sellRateZoneId: string;
  mapped: boolean;
}

export interface CarrierZoneToSellRateZoneMapping {
  carrierId: string;
  carrierServiceMethodId: string;
  zoneId: string;
  sellRateCarrierId: string;
  sellRateCarrierServiceMethodId: string;
  sellRateZoneId: string;
  partnerId: string;
}

export interface SearchCarrierZoneToSellRateZoneMappingParams {
  carrierId?: string;
  zoneId?: string;
  sellRateCarrierServiceMethodId?: string;
  sellRateZoneId?: string;
  page?: number;
  pageSize?: number;
}

export interface DeleteSellRateMappingsRes {
  deletedRecords: number;
  sellRateCarrierServiceMethodId: string;
  carrierId: string;
  zoneId: string;
  sellRateZoneId: string;
}

export const TNT_MODEL_SOURCE_MAPPING: Partial<Record<TimeInTransitModelSource, string>> = {
  shipment_delivery_prediction: 'Shipment Delivery Prediction',
  shipment_arrival_estimate: 'Shipment Arrival Estimate',
} as const;

// Labels above cover every value that can arrive from a partner config or a persisted simulation.
// This list is the narrower set a customer may choose from.
export const SELECTABLE_TNT_MODEL_SOURCES: TimeInTransitModelSource[] = ['shipment_delivery_prediction'];

export const TNT_SOURCE_PREFERENCE_MAPPING: Partial<Record<TimeInTransitSourcePreference, string>> = {
  partner_provided: 'Partner Provided',
  shipium_calculated: 'Shipium Calculated',
  carrier_default: 'Carrier Default',
};

export const TNT_SETTING_MAPPING: Partial<Record<TimeInTransitSetting, string>> = {
  aggressive: 'Aggressive',
  standard: 'Standard',
  conservative: 'Conservative',
  very_conservative: 'Very Conservative',
};

export enum SimulationContextLinkValidationMessage {
  NO_SCHEDULE = 'No schedule is configured for this context.',
  NO_SCHEDULE_OVERLAP = 'No schedule entry overlaps with the shipment dates. Please ensure the context schedule covers the shipment window.',
  NO_CARRIER_RATES = 'No carrier contracts or rates are configured for this context.',
  NO_CARRIER_RATES_OVERLAP = 'No carrier rate calendar entry overlaps with the shipment dates. Please ensure at least one carrier rate is active for the shipment window.',
}
